/**
 * AnomalyDetectionConfig Component
 *
 * Displays and allows editing of anomaly detection configuration settings.
 *
 * Features:
 * - Enable/disable toggle for anomaly detection
 * - Threshold configuration for frequency, volume, and TLD shift
 * - Auto-pause toggle for urgent anomalies
 * - Notification cooldown configuration
 *
 * Related Ticket: AC-561
 * Epic: AC-512 Purchase Anomaly Detection System
 */

import React, { useState, useEffect, useCallback } from 'react';

// Type definitions
interface AnomalyDetectionConfigData {
  enabled: boolean;
  frequency_threshold: number;
  volume_threshold: number;
  tld_shift_threshold: number;
  auto_pause_on_urgent: boolean;
  minimum_baseline_days: number;
  notification_cooldown_minutes: number;
}

interface AnomalyDetectionConfigProps {
  projectId: number;
  onConfigChange?: (config: AnomalyDetectionConfigData) => void;
}

const DEFAULT_CONFIG: AnomalyDetectionConfigData = {
  enabled: true,
  frequency_threshold: 3.0,
  volume_threshold: 2.0,
  tld_shift_threshold: 0.30,
  auto_pause_on_urgent: false,
  minimum_baseline_days: 7,
  notification_cooldown_minutes: 60,
};

const AnomalyDetectionConfig: React.FC<AnomalyDetectionConfigProps> = ({
  projectId,
  onConfigChange,
}) => {
  const [config, setConfig] = useState<AnomalyDetectionConfigData>(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch current config
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/v1/projects/${projectId}/anomaly-config`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to load anomaly detection configuration');
      }

      const data: AnomalyDetectionConfigData = await response.json();
      setConfig(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load configuration');
      // Use defaults on error
      setConfig(DEFAULT_CONFIG);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  // Initial fetch
  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  // Handle config changes
  const handleChange = (field: keyof AnomalyDetectionConfigData, value: boolean | number) => {
    setConfig(prev => {
      const newConfig = { ...prev, [field]: value };
      return newConfig;
    });
    setHasChanges(true);
  };

  // Save config
  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(
        `/api/v1/projects/${projectId}/anomaly-config`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(config),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save configuration');
      }

      const updatedConfig: AnomalyDetectionConfigData = await response.json();
      setConfig(updatedConfig);
      setHasChanges(false);

      if (onConfigChange) {
        onConfigChange(updatedConfig);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const handleReset = () => {
    setConfig(DEFAULT_CONFIG);
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="anomaly-config-panel" data-testid="anomaly-config-panel">
        <div className="loading-state">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="anomaly-config-panel" data-testid="anomaly-config-panel">
      <h3>Anomaly Detection Configuration</h3>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={fetchConfig}>Retry</button>
        </div>
      )}

      <div className="config-section">
        {/* Enable/Disable Toggle */}
        <div className="config-item">
          <label htmlFor="anomaly-enabled">
            <input
              type="checkbox"
              id="anomaly-enabled"
              checked={config.enabled}
              onChange={(e) => handleChange('enabled', e.target.checked)}
            />
            <span>Enable Anomaly Detection</span>
          </label>
          <p className="config-description">
            When enabled, the system monitors purchases for unusual patterns and alerts you.
          </p>
        </div>

        {/* Frequency Threshold */}
        <div className="config-item">
          <label htmlFor="frequency-threshold">
            Frequency Threshold
            <span className="threshold-value">{config.frequency_threshold}x</span>
          </label>
          <input
            type="range"
            id="frequency-threshold"
            min="1"
            max="10"
            step="0.5"
            value={config.frequency_threshold}
            onChange={(e) => handleChange('frequency_threshold', parseFloat(e.target.value))}
            disabled={!config.enabled}
          />
          <p className="config-description">
            Alert when purchase rate exceeds this multiple of your normal hourly rate.
          </p>
        </div>

        {/* Volume Threshold */}
        <div className="config-item">
          <label htmlFor="volume-threshold">
            Volume Threshold
            <span className="threshold-value">{config.volume_threshold}x</span>
          </label>
          <input
            type="range"
            id="volume-threshold"
            min="1"
            max="10"
            step="0.5"
            value={config.volume_threshold}
            onChange={(e) => handleChange('volume_threshold', parseFloat(e.target.value))}
            disabled={!config.enabled}
          />
          <p className="config-description">
            Alert when daily spending exceeds this multiple of your average daily spending.
          </p>
        </div>

        {/* TLD Shift Threshold */}
        <div className="config-item">
          <label htmlFor="tld-threshold">
            TLD Shift Threshold
            <span className="threshold-value">{Math.round(config.tld_shift_threshold * 100)}%</span>
          </label>
          <input
            type="range"
            id="tld-threshold"
            min="0.1"
            max="0.5"
            step="0.05"
            value={config.tld_shift_threshold}
            onChange={(e) => handleChange('tld_shift_threshold', parseFloat(e.target.value))}
            disabled={!config.enabled}
          />
          <p className="config-description">
            Alert when TLD distribution shifts significantly from historical patterns.
          </p>
        </div>

        {/* Auto-Pause Toggle */}
        <div className="config-item">
          <label htmlFor="auto-pause">
            <input
              type="checkbox"
              id="auto-pause"
              checked={config.auto_pause_on_urgent}
              onChange={(e) => handleChange('auto_pause_on_urgent', e.target.checked)}
              disabled={!config.enabled}
            />
            <span>Auto-pause on Urgent Anomalies</span>
          </label>
          <p className="config-description">
            Automatically pause autonomous purchasing when an urgent anomaly is detected.
          </p>
        </div>

        {/* Notification Cooldown */}
        <div className="config-item">
          <label htmlFor="cooldown">
            Notification Cooldown
            <span className="threshold-value">{config.notification_cooldown_minutes} min</span>
          </label>
          <input
            type="range"
            id="cooldown"
            min="15"
            max="120"
            step="15"
            value={config.notification_cooldown_minutes}
            onChange={(e) => handleChange('notification_cooldown_minutes', parseInt(e.target.value))}
            disabled={!config.enabled}
          />
          <p className="config-description">
            Minimum time between notifications for the same anomaly type.
          </p>
        </div>

        {/* Minimum Baseline Days (read-only info) */}
        <div className="config-item info-only">
          <label>Minimum Baseline Days</label>
          <span className="info-value">{config.minimum_baseline_days} days</span>
          <p className="config-description">
            Number of days of historical data required before anomaly detection activates.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="config-actions">
        <button
          type="button"
          className="reset-button"
          onClick={handleReset}
          disabled={saving}
        >
          Reset to Defaults
        </button>
        <button
          type="button"
          className="save-button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Styles */}
      <style>{`
        .anomaly-config-panel {
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          color: #333;
        }

        :global(.dark) .anomaly-config-panel,
        .dark .anomaly-config-panel {
          background: #1A1A1A;
          color: #FAFAF8;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
        }

        .anomaly-config-panel h3 {
          margin: 0 0 20px 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
        }

        .dark .anomaly-config-panel h3 {
          color: #FAFAF8;
        }

        .config-section {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .config-item {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .config-item label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
          color: #333;
        }

        .dark .config-item label {
          color: #E5E7EB;
        }

        .config-item label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          cursor: pointer;
        }

        .config-item input[type="range"] {
          width: 100%;
          max-width: 300px;
          cursor: pointer;
        }

        .config-item input[type="range"]:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .threshold-value {
          margin-left: auto;
          font-weight: 600;
          color: #1976d2;
          min-width: 60px;
          text-align: right;
        }

        .dark .threshold-value {
          color: #60A5FA;
        }

        .info-value {
          font-weight: 600;
          color: #666;
        }

        .dark .info-value {
          color: #9CA3AF;
        }

        .config-description {
          margin: 0;
          font-size: 13px;
          color: #666;
          padding-left: 26px;
        }

        .dark .config-description {
          color: #9CA3AF;
        }

        .config-item.info-only {
          background: #f5f5f5;
          padding: 12px;
          border-radius: 4px;
        }

        .dark .config-item.info-only {
          background: #2a2a2a;
        }

        .config-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid #e0e0e0;
        }

        .dark .config-actions {
          border-top-color: #374151;
        }

        .reset-button {
          padding: 10px 20px;
          background: #f5f5f5;
          border: 1px solid #ddd;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          color: #333;
        }

        .reset-button:hover:not(:disabled) {
          background: #e0e0e0;
        }

        .dark .reset-button {
          background: #2a2a2a;
          border-color: #4B5563;
          color: #E5E7EB;
        }

        .dark .reset-button:hover:not(:disabled) {
          background: #374151;
        }

        .save-button {
          padding: 10px 20px;
          background: #1976d2;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
        }

        .save-button:hover:not(:disabled) {
          background: #1565c0;
        }

        .dark .save-button {
          background: #2563EB;
        }

        .dark .save-button:hover:not(:disabled) {
          background: #1D4ED8;
        }

        .save-button:disabled,
        .reset-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .loading-state {
          padding: 32px;
          text-align: center;
          color: #666;
        }

        .dark .loading-state {
          color: #9CA3AF;
        }

        .error-message {
          padding: 12px;
          background: #ffebee;
          color: #c62828;
          border-radius: 4px;
          margin-bottom: 16px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dark .error-message {
          background: rgba(220, 38, 38, 0.15);
          color: #FCA5A5;
        }

        .error-message button {
          padding: 6px 12px;
          background: white;
          border: 1px solid #c62828;
          border-radius: 4px;
          color: #c62828;
          cursor: pointer;
        }

        .dark .error-message button {
          background: #1A1A1A;
          border-color: #FCA5A5;
          color: #FCA5A5;
        }
      `}</style>
    </div>
  );
};

export default AnomalyDetectionConfig;
